import { useState } from "react";
import { useNavigate } from "react-router";
import { AuthService } from "@/hooks/services/authService";
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Users } from "lucide-react";
import { AuthStore } from "@/hooks/stores/AuthStore";
import config from "../../../config";

export default function ParentSignInPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (config.appMode === "prototype") {
        // Prototype: simulate successful login
        AuthStore.getState().login(true, "parent", { name: "Demo Parent", email: formData.email, userType: "parent" }, "mock-token", "mock-refresh");
        setSuccess(true);
        setTimeout(() => navigate("/parent"), 1200);
        return;
      }
      const { user, accessToken, refreshToken } = await AuthService.login({
        email: formData.email,
        password: formData.password,
        userType: "parent",
      });
      AuthStore.getState().login(true, user.userType, user, accessToken, refreshToken);
      setSuccess(true);
      setTimeout(() => navigate("/parent"), 1200);
    } catch (err) {
      setError(err?.response?.data?.error?.message || err.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/20 overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-violet-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="w-full flex flex-col lg:flex-row relative z-10">
        {/* Brand panel */}
        <div className="hidden lg:flex w-1/2 items-center justify-center p-10 bg-gradient-to-br from-violet-600 to-indigo-700">
          <div className="text-white text-center space-y-6 max-w-sm">
            <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto">
              <Users className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold">Parent Portal</h2>
            <p className="text-violet-200 text-base leading-relaxed">
              View your child's academic progress, career journey, goals, and achievements in real time.
            </p>
            <div className="flex flex-col gap-3 mt-6 text-sm text-violet-100">
              {["Track academic performance", "Monitor goal completion", "View career path progress", "Stay connected with learning"].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-violet-300 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form panel */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md space-y-7">
            <button onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-medium transition-colors">
              <Users className="w-4 h-4" />
              Parent Portal
              <span className="text-slate-300">·</span>
              <span className="text-violet-600 hover:underline">Change portal</span>
            </button>

            <div className="space-y-1">
              <h1 className="text-3xl font-bold text-slate-900">Parent sign in</h1>
              <p className="text-slate-500 text-sm">Access your child's learning dashboard</p>
            </div>

            <div className="backdrop-blur-xl bg-white/50 border border-white/70 rounded-3xl p-8 sm:p-10 shadow-2xl">
              {success && (
                <div className="mb-6 p-4 bg-green-50/80 border border-green-200/50 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  <span className="text-green-800 font-medium text-sm">Signing in…</span>
                </div>
              )}
              {error && !success && (
                <div className="mb-6 p-4 bg-red-50/80 border border-red-200/50 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-red-800 font-medium text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Email address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="email" name="email" placeholder="parent@example.com"
                      value={formData.email} onChange={handleChange} disabled={isLoading || success}
                      className="w-full pl-11 pr-4 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50" />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-semibold text-slate-700">Password</label>
                    <a href="/forgot-password" className="text-xs text-violet-600 hover:text-violet-700 font-medium">Forgot password?</a>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type={showPassword ? "text" : "password"} name="password" placeholder="••••••••"
                      value={formData.password} onChange={handleChange} disabled={isLoading || success}
                      className="w-full pl-11 pr-11 py-3.5 bg-white/70 border border-slate-200/80 rounded-xl focus:bg-white focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 text-sm disabled:opacity-50" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={isLoading || success}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-semibold text-sm shadow-lg transition-all disabled:opacity-50">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing in…
                    </span>
                  ) : success ? "Redirecting…" : "Sign in"}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-slate-400">
                Account set up by your child's school.{" "}
                <a href="mailto:support@earlyscape.io" className="text-violet-600 hover:underline font-medium">Contact support</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
